import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
    Paper, TextField, Button, Typography, Divider, Chip, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, InputAdornment, CircularProgress, Dialog,
    DialogTitle, DialogContent, DialogActions, Grow,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import LinearProgress from '@material-ui/core/LinearProgress'
import Skeleton from '@material-ui/lab/Skeleton'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import CropFreeIcon from '@material-ui/icons/CropFree'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import PersonIcon from '@material-ui/icons/Person'
import StorefrontIcon from '@material-ui/icons/Storefront'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import LockIcon from '@material-ui/icons/Lock'
import FindInPageIcon from '@material-ui/icons/FindInPage'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import TouchAppIcon from '@material-ui/icons/TouchApp'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import HttpService from '../../services/httpService'
import { setSnackInfo } from '../../redux/actions/appAction'

const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const INK = '#3d2b21'
const GREEN = '#1e7e34'
const GREEN_LIGHT = '#e6f4ea'
const RED = '#b3261e'
const RED_LIGHT = '#fdecea'
const AMBER = '#b26a00'
const AMBER_LIGHT = '#fff3e0'
const GREY = '#5f6b76'
const GREY_LIGHT = '#eef1f4'

const RESULT_COLUMNS = 5

// Mirrors the server's qrStatus(). The server is the authority - this only
// decides how a row is painted.
const STATUS_META = {
    ok: {
        label: 'Correct', color: GREEN, bg: GREEN_LIGHT, Icon: CheckCircleIcon,
        title: 'This QR encodes the lead’s own number',
        detail: 'Payments scanned from it will reconcile to this lead. Regenerating is safe but unnecessary.',
    },
    mismatch: {
        label: 'Wrong lead', color: RED, bg: RED_LIGHT, Icon: ReportProblemIcon,
        title: 'This QR was built from a different lead’s number',
        detail: 'A customer scanning it pays into another lead’s virtual account, and the payment reconciles to that lead. Regenerate it.',
    },
    missing: {
        label: 'No QR', color: AMBER, bg: AMBER_LIGHT, Icon: ErrorOutlineIcon,
        title: 'This lead has no payment QR yet',
        detail: 'One is normally built on the next quotation save. Regenerate here if it is needed sooner.',
    },
    unknown: {
        label: 'Unreadable', color: GREY, bg: GREY_LIGHT, Icon: HelpOutlineIcon,
        title: 'The stored QR url does not follow either known naming pattern',
        detail: 'Which lead number it encodes cannot be read from the url, so it cannot be verified. Regenerating replaces it with a known-good one.',
    },
    noNumber: {
        label: 'No lead number', color: GREY, bg: GREY_LIGHT, Icon: HelpOutlineIcon,
        title: 'This lead has no uniqueLeadNumber',
        detail: 'There is nothing to encode in a QR until the lead is assigned one.',
    },
}

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress)

const useStyles = makeStyles((theme) => ({
    root: { padding: '4px 4px 32px' },
    headerBanner: {
        display: 'flex', alignItems: 'center', gap: 16,
        background: HEADER_GRADIENT,
        borderRadius: 16, padding: '16px 26px', marginBottom: 16, color: '#fff',
        boxShadow: '0 8px 24px rgba(93,55,28,0.18)',
        [theme.breakpoints.down('xs')]: { padding: '16px 18px', flexWrap: 'wrap' },
    },
    headerIconWrap: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 46, height: 46, borderRadius: 14, flexShrink: 0,
        background: 'rgba(255,255,255,0.18)',
    },
    headerTitle: { fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.3 },
    headerSubtitle: { fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4, maxWidth: 760 },
    restrictedChip: {
        marginLeft: 'auto', flexShrink: 0,
        background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700,
        [theme.breakpoints.down('xs')]: { marginLeft: 0 },
    },
    progressSlot: { height: 4, marginBottom: 10 },
    loadingBanner: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 14px', marginBottom: 14, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
    },
    loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
    loadingBannerHint: { fontSize: 11.5, color: '#7a6a60', marginTop: 1 },
    card: {
        borderRadius: 16, border: '1px solid #eceff3',
        boxShadow: '0 4px 24px rgba(93,55,28,0.08)',
        padding: '18px 20px', marginBottom: 18,
    },
    sectionLabel: {
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 13, fontWeight: 700, color: ORANGE_DARK,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 14,
    },
    searchRow: { display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' },
    searchField: { flex: 1, minWidth: 300 },
    searchInputRoot: { borderRadius: 24, background: '#f7f9fc' },
    searchBtn: {
        background: ORANGE_DARK, color: '#fff', fontWeight: 700,
        borderRadius: 24, textTransform: 'none', padding: '8px 26px', marginTop: 1,
        boxShadow: '0 4px 14px rgba(193,83,31,0.25)',
        '&:hover': { background: '#a8441a' },
        '&.Mui-disabled': { background: 'rgba(193,83,31,0.45)', color: '#fff' },
    },
    tableWrap: { borderRadius: 12, border: '1px solid #eceff3', overflow: 'hidden' },
    headCell: {
        backgroundColor: ORANGE_DARK, color: '#fff', fontWeight: 700,
        fontSize: 12.5, letterSpacing: 0.3, borderBottom: 'none', whiteSpace: 'nowrap',
    },
    bodyCell: { fontSize: 13.5, color: INK },
    resultRow: {
        cursor: 'pointer', transition: 'background-color 150ms ease',
        '&:nth-of-type(even)': { backgroundColor: '#fafbfd' },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
    },
    selectedRow: {
        backgroundColor: `${ORANGE_LIGHT} !important`,
        boxShadow: `inset 3px 0 0 ${ORANGE_DARK}`,
    },
    pickHint: {
        display: 'flex', alignItems: 'center', gap: 9,
        margin: '0 0 12px', padding: '10px 14px', borderRadius: 11,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}55`,
        fontSize: 12.5, color: '#8a4a1c', lineHeight: 1.5,
    },
    openChip: {
        display: 'inline-flex', alignItems: 'center', gap: 2,
        fontSize: 11.5, fontWeight: 700, color: ORANGE_DARK,
        background: '#fff', border: `1px solid ${ORANGE}66`,
        borderRadius: 999, padding: '2px 6px 2px 10px', whiteSpace: 'nowrap',
    },
    openChipOn: {
        display: 'inline-flex', alignItems: 'center', gap: 2,
        fontSize: 11.5, fontWeight: 800, color: '#fff',
        background: ORANGE_DARK, borderRadius: 999,
        padding: '2px 6px 2px 10px', whiteSpace: 'nowrap',
    },
    customerCell: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
    avatarChip: {
        width: 28, height: 28, fontSize: 12, fontWeight: 700,
        background: ORANGE_LIGHT, color: ORANGE_DARK,
    },
    mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
    emptyState: { textAlign: 'center', padding: '54px 20px', color: '#9aa5b1' },
    emptyTitle: { fontSize: 15, fontWeight: 700, color: INK, marginTop: 10 },
    emptyHint: { fontSize: 13, color: '#8b7a6f', marginTop: 4 },
    searchHint: {
        display: 'flex', alignItems: 'center', minHeight: 18,
        marginTop: 8, fontSize: 12, color: '#8b7a6f',
    },
    searchHintWarn: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontWeight: 700, color: '#9c4a16',
    },
    hintChips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 },
    hintChip: { background: '#f4f6fb', color: '#6b7280', fontWeight: 600, fontSize: 12 },

    detailGrid: {
        display: 'grid', gap: 18,
        gridTemplateColumns: 'minmax(0,1fr) 260px',
        [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0,1fr)' },
    },
    factRow: { display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 16 },
    fact: { minWidth: 150 },
    factLabel: {
        fontSize: 11, fontWeight: 700, color: '#9a8b80',
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3,
    },
    factValue: { fontSize: 14, fontWeight: 600, color: INK, wordBreak: 'break-word' },

    statusPanel: {
        display: 'flex', gap: 12, alignItems: 'flex-start',
        borderRadius: 12, padding: '13px 16px', marginBottom: 16,
    },
    statusTitle: { fontSize: 13.5, fontWeight: 700 },
    statusDetail: { fontSize: 12.5, marginTop: 3, lineHeight: 1.5, color: '#5b4b42' },

    // Shown in place of the remarks box and the button when the QR is already
    // correct, so the panel does not simply end on a disabled control.
    settledPanel: {
        display: 'flex', gap: 12, alignItems: 'flex-start',
        borderRadius: 12, padding: '14px 16px',
        background: GREEN_LIGHT, border: `1px solid ${GREEN}22`,
    },

    vpaBox: {
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#f7f9fc', border: '1px dashed #d8dee6',
        borderRadius: 10, padding: '10px 14px', marginBottom: 16,
    },
    vpaValue: { fontSize: 13.5, fontWeight: 700, color: INK, wordBreak: 'break-all' },

    urlBox: {
        background: '#f7f9fc', border: '1px solid #eceff3', borderRadius: 10,
        padding: '10px 14px', fontSize: 12, color: '#55606b',
        wordBreak: 'break-all', marginBottom: 6, lineHeight: 1.6,
    },

    qrPane: {
        border: '1px solid #eceff3', borderRadius: 14, padding: 14,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        background: '#fcfdfe', alignSelf: 'start',
    },
    qrImg: {
        width: 200, height: 200, objectFit: 'contain',
        borderRadius: 10, background: '#fff', border: '1px solid #eef1f4',
    },
    qrPlaceholder: {
        width: 200, height: 200, borderRadius: 10,
        border: '1px dashed #d8dee6', background: '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 8, color: '#9aa6b1', fontSize: 12.5,
        textAlign: 'center', padding: 12,
    },
    qrCaption: { fontSize: 11.5, color: '#8b7a6f', textAlign: 'center' },

    remarksField: { marginTop: 4 },
    actionRow: {
        display: 'flex', gap: 12, alignItems: 'center',
        marginTop: 16, flexWrap: 'wrap',
    },
    regenerateBtn: {
        background: ORANGE_DARK, color: '#fff', fontWeight: 700,
        borderRadius: 24, textTransform: 'none', padding: '9px 28px',
        boxShadow: '0 4px 14px rgba(193,83,31,0.25)',
        '&:hover': { background: '#a8441a' },
        '&.Mui-disabled': { background: 'rgba(193,83,31,0.35)', color: '#fff' },
    },
    dangerNote: { fontSize: 12, color: RED, display: 'flex', alignItems: 'center', gap: 6 },

    dialogPaper: { borderRadius: 16, maxWidth: 520 },
    dialogTitle: { fontSize: 17, fontWeight: 700, color: INK, paddingBottom: 4 },
    dialogLine: { fontSize: 13.5, color: '#55606b', lineHeight: 1.65, marginBottom: 10 },
    dialogCompare: {
        background: '#f7f9fc', borderRadius: 10, padding: '12px 14px',
        fontSize: 12.5, lineHeight: 1.8, marginBottom: 6,
    },
    confirmBtn: {
        background: ORANGE_DARK, color: '#fff', fontWeight: 700,
        borderRadius: 20, textTransform: 'none', padding: '7px 22px',
        '&:hover': { background: '#a8441a' },
    },
    cancelBtn: { textTransform: 'none', fontWeight: 600, color: '#6b7781', borderRadius: 20 },
}))

const initials = (name) => String(name || '?').trim().charAt(0).toUpperCase() || '?'

const PaymentQrCode = (props) => {
    const classes = useStyles()

    const [term, setTerm] = useState('')
    const [searching, setSearching] = useState(false)
    const [searched, setSearched] = useState(false)
    const [searchedFor, setSearchedFor] = useState('')
    const [results, setResults] = useState([])

    const [lead, setLead] = useState(null)
    const [loadingLead, setLoadingLead] = useState(false)

    const [remarks, setRemarks] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [previous, setPrevious] = useState(null)

    const meta = useMemo(
        () => STATUS_META[lead?.status] || STATUS_META.unknown,
        [lead],
    )

    const busy = searching || loadingLead || saving

    // The lead search runs over 1.2M records without an index on every field
    // it looks at, so it reliably takes about five seconds. Saying so beats a
    // bare bar that looks stuck.
    const busyText = useMemo(() => {
        if (saving) {
            return {
                title: 'Building and uploading the new QR\u2026',
                hint: 'Generating the image, uploading it, then pointing the lead at it.',
            }
        }
        if (loadingLead) {
            return {
                title: 'Fetching this lead\u2019s QR details\u2026',
                hint: 'Reading the stored QR so its lead number can be checked.',
            }
        }
        return {
            title: 'Searching leads\u2026',
            hint: 'This usually takes around five seconds \u2014 the lead search scans a large table.',
        }
    }, [searching, loadingLead, saving])

    const searchLeads = async () => {
        if (term.trim().length < 3) {
            props.setSnackInfo('Type at least 3 characters to search', 'warning')
            return
        }
        setSearching(true)
        try {
            // Reuses the Design Sign-Off lead lookup - same collection, same
            // role list - instead of a second search endpoint that would drift.
            // It returns lead identity only, so QR state is fetched per lead
            // when one is picked.
            const res = await HttpService.searchDesignSignOffLeads(term.trim())
            setResults(res.data.leads || [])
            setSearched(true)
            setSearchedFor(term.trim())
            setLead(null)
            setPrevious(null)
            setRemarks('')
            if (!res.data.leads?.length) props.setSnackInfo('No lead found for that search', 'warning')
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Something went wrong', 'error')
        }
        setSearching(false)
    }

    const pickLead = async (row) => {
        setLoadingLead(true)
        setPrevious(null)
        setRemarks('')
        try {
            // Re-read rather than trusting the row: the search result may be a
            // few minutes old, and this is the value the regenerate decision is
            // made against.
            const res = await HttpService.getPaymentQrLead(row._id)
            setLead(res.data.lead)
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not load the lead', 'error')
        }
        setLoadingLead(false)
    }

    const regenerate = async () => {
        setSaving(true)
        try {
            const res = await HttpService.regeneratePaymentQr(lead._id, { remarks: remarks.trim() })
            setPrevious({
                url: res.data.previousQrCode,
                encoded: res.data.previousEncodedLeadNumber,
            })
            setLead(res.data.lead)
            setResults((rows) => rows.map((r) => (r._id === res.data.lead._id ? res.data.lead : r)))
            setRemarks('')
            setConfirmOpen(false)
            props.setSnackInfo(res.data.message || 'Payment QR code regenerated', 'success')
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not regenerate the QR code', 'error')
        }
        setSaving(false)
    }

    const copy = (value, what) => {
        if (!value) return
        navigator.clipboard?.writeText(value)
            .then(() => props.setSnackInfo(`${what} copied`, 'success'))
            .catch(() => props.setSnackInfo('Could not copy', 'warning'))
    }

    const StatusChip = ({ status }) => {
        const m = STATUS_META[status] || STATUS_META.unknown
        return (
            <Chip
                size="small"
                icon={<m.Icon style={{ color: m.color, fontSize: 16 }} />}
                label={m.label}
                style={{ background: m.bg, color: m.color, fontWeight: 700, fontSize: 12 }}
            />
        )
    }

    // 'ok' is excluded as well as 'noNumber': a QR that already encodes this
    // lead's own number is correct, and the controls for it are not rendered
    // at all. This keeps the guard honest even if that changes.
    const canRegenerate = lead
        && lead.status !== 'noNumber'
        && lead.status !== 'ok'
        && remarks.trim().length >= 5
        && !saving

    return (
        <div className={classes.root}>
            <div className={classes.headerBanner}>
                <div className={classes.headerIconWrap}>
                    <CropFreeIcon style={{ fontSize: 26, color: '#fff' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <Typography className={classes.headerTitle}>Payment QR Code</Typography>
                    <Typography className={classes.headerSubtitle}>
                        Rebuild the UPI payment QR for a lead. The QR encodes the lead&rsquo;s virtual
                        account, so one built from the wrong number sends the customer&rsquo;s money to
                        a different deal.
                    </Typography>
                </div>
                <Chip
                    className={classes.restrictedChip}
                    icon={<LockIcon style={{ color: '#fff', fontSize: 16 }} />}
                    label="Admin / Naraci Tech"
                    size="small"
                />
            </div>

            <div className={classes.progressSlot}>
                {busy && <BrandLinearProgress />}
            </div>

            {busy && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={16} style={{ color: ORANGE_DARK }} />
                    <div>
                        <div className={classes.loadingBannerTitle}>{busyText.title}</div>
                        <div className={classes.loadingBannerHint}>{busyText.hint}</div>
                    </div>
                </div>
            )}

            {/* ---------------------------------------------------------- search */}
            <Paper elevation={0} className={classes.card}>
                <div className={classes.sectionLabel}>
                    <FindInPageIcon style={{ fontSize: 17 }} />
                    Find the lead
                </div>
                <div className={classes.searchRow}>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        size="small"
                        placeholder="Project number or unique lead number — e.g. KA0226297"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') searchLeads() }}
                        InputProps={{
                            classes: { root: classes.searchInputRoot },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: ORANGE_DARK, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: term ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setTerm(''); setResults([]); setSearched(false); setSearchedFor(''); setLead(null) }}>
                                        <ClearIcon style={{ fontSize: 17 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Button
                        className={classes.searchBtn}
                        onClick={searchLeads}
                        disabled={searching || term.trim().length < 3}
                        startIcon={searching ? <CircularProgress size={15} style={{ color: '#fff' }} /> : <SearchIcon />}
                    >
                        {searching ? 'Searching' : 'Search'}
                    </Button>
                </div>

                {/* Says what the button is waiting for. The Search button
                    disables under three characters, and a disabled button with
                    no reason beside it is what gets reported as broken. */}
                <div className={classes.searchHint}>
                    {term.trim().length > 0 && term.trim().length < 3 ? (
                        <span className={classes.searchHintWarn}>
                            <InfoOutlinedIcon style={{ fontSize: 14 }} />
                            {`${3 - term.trim().length} more character${3 - term.trim().length === 1 ? '' : 's'} to search`}
                        </span>
                    ) : (
                        <span>Type at least 3 characters, then press Enter</span>
                    )}
                </div>
            </Paper>

            {/* --------------------------------------------------------- results */}
            <Paper elevation={0} className={classes.card}>
                <div className={classes.sectionLabel}>
                    <CropFreeIcon style={{ fontSize: 17 }} />
                    {searching
                        ? 'Searching…'
                        : searched
                            ? `${results.length} lead${results.length === 1 ? '' : 's'} found`
                            : 'Find a lead'}
                </div>

                {/* The rows have been clickable all along, but nothing said
                        so - people searched, got a table, and stopped. */}
                {!searching && results.length > 0 && (
                    <div className={classes.pickHint}>
                        <TouchAppIcon style={{ fontSize: 17 }} />
                        <span>
                            <b>Click a row</b> to see that lead&apos;s payment QR code — and to replace it
                            if it is wrong.
                        </span>
                    </div>
                )}
                <TableContainer className={classes.tableWrap}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>Project No</TableCell>
                                <TableCell className={classes.headCell}>Unique Lead No.</TableCell>
                                <TableCell className={classes.headCell}>Customer</TableCell>
                                <TableCell className={classes.headCell}>Current Stage</TableCell>
                                <TableCell className={classes.headCell}>Lead No</TableCell>
                                <TableCell className={classes.headCell} align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {searching && [0, 1, 2].map((i) => (
                                <TableRow key={i}>
                                    {Array.from({ length: RESULT_COLUMNS }).map((_, c) => (
                                        <TableCell key={c}><Skeleton variant="text" /></TableCell>
                                    ))}
                                </TableRow>
                            ))}

                            {!searching && results.map((row) => (
                                <TableRow
                                    key={row._id}
                                    className={`${classes.resultRow} ${lead?._id === row._id ? classes.selectedRow : ''}`}
                                    onClick={() => pickLead(row)}
                                >
                                    <TableCell className={`${classes.bodyCell} ${classes.mono}`} style={{ fontWeight: 700, color: ORANGE_DARK }}>
                                        {row.erpProjectNo || '—'}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>{row.uniqueLeadNumber || '-'}</TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        <span className={classes.customerCell}>
                                            <Avatar className={classes.avatarChip}>{initials(row.customerName)}</Avatar>
                                            {row.customerName || '—'}
                                        </span>
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>{row.currentStage || '—'}</TableCell>
                                    <TableCell className={`${classes.bodyCell} ${classes.mono}`}>
                                        {row.lead_no ?? '—'}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell} align="right">
                                        <span className={lead?._id === row._id ? classes.openChipOn : classes.openChip}>
                                            {lead?._id === row._id ? 'Open' : 'View QR'}
                                            <ChevronRightIcon style={{ fontSize: 15 }} />
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {!searching && !results.length && (
                                <TableRow>
                                    <TableCell colSpan={RESULT_COLUMNS} style={{ borderBottom: 'none' }}>
                                        {/* Two different situations, two different
                                                things worth saying. */}
                                        <div className={classes.emptyState}>
                                            {searched ? (
                                                <>
                                                    <FindInPageIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                                                    <Typography className={classes.emptyTitle}>
                                                        Nothing matched “{searchedFor}”
                                                    </Typography>
                                                    <Typography className={classes.emptyHint}>
                                                        Check the project number, or try the unique lead number instead.
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <SearchIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                                                    <Typography className={classes.emptyTitle}>
                                                        Search for a project to begin
                                                    </Typography>
                                                    <Typography className={classes.emptyHint}>
                                                        Its payment QR code will appear here, ready to check and replace.
                                                    </Typography>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!searched && !searching && (
                    <div className={classes.hintChips}>
                        <Chip size="small" className={classes.hintChip} label="Searches project numbers" />
                        <Chip size="small" className={classes.hintChip} label="Searches unique lead numbers" />
                        <Chip size="small" className={classes.hintChip} label="Shows the 20 most recent matches" />
                    </div>
                )}
            </Paper>

            {/* ---------------------------------------------------------- detail */}
            {loadingLead && (
                <Paper elevation={0} className={classes.card}>
                    <Skeleton variant="text" width={240} height={28} />
                    <Skeleton variant="rect" height={190} style={{ borderRadius: 12, marginTop: 12 }} />
                </Paper>
            )}

            {!loadingLead && lead && (
                <Grow in timeout={260}>
                    <Paper elevation={0} className={classes.card}>
                        <div className={classes.sectionLabel}>
                            <AccountBalanceWalletIcon style={{ fontSize: 17 }} />
                            Payment QR for {lead.erpProjectNo || lead.uniqueLeadNumber}
                            {/* The search response carries lead identity only, so the
                                QR verdict appears here, once the lead is loaded. */}
                            <span style={{ marginLeft: 'auto' }}>
                                <StatusChip status={lead.status} />
                            </span>
                        </div>

                        <div className={classes.detailGrid}>
                            <div style={{ minWidth: 0 }}>
                                <div className={classes.factRow}>
                                    <div className={classes.fact}>
                                        <div className={classes.factLabel}>Customer</div>
                                        <div className={classes.factValue}>
                                            <PersonIcon style={{ fontSize: 15, color: '#b7a79c', marginRight: 5, verticalAlign: -2 }} />
                                            {lead.customerName || '—'}
                                        </div>
                                    </div>
                                    <div className={classes.fact}>
                                        <div className={classes.factLabel}>Experience Centre</div>
                                        <div className={classes.factValue}>
                                            <StorefrontIcon style={{ fontSize: 15, color: '#b7a79c', marginRight: 5, verticalAlign: -2 }} />
                                            {lead.experienceCenterName || '—'}
                                        </div>
                                    </div>
                                    <div className={classes.fact}>
                                        <div className={classes.factLabel}>Unique Lead Number</div>
                                        <div className={`${classes.factValue} ${classes.mono}`}>{lead.uniqueLeadNumber || '—'}</div>
                                    </div>
                                    <div className={classes.fact}>
                                        <div className={classes.factLabel}>Stage</div>
                                        <div className={classes.factValue}>{lead.currentStage || '—'}</div>
                                    </div>
                                </div>

                                <div className={classes.statusPanel} style={{ background: meta.bg }}>
                                    <meta.Icon style={{ color: meta.color, fontSize: 21, flexShrink: 0, marginTop: 1 }} />
                                    <div>
                                        <div className={classes.statusTitle} style={{ color: meta.color }}>{meta.title}</div>
                                        <div className={classes.statusDetail}>{meta.detail}</div>
                                        {lead.status === 'mismatch' && (
                                            <div className={`${classes.statusDetail} ${classes.mono}`} style={{ marginTop: 6 }}>
                                                lead <b>{lead.uniqueLeadNumber}</b> &nbsp;&larr;&nbsp; QR built from <b>{lead.qrEncodesLeadNumber}</b>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {lead.upiVpa && (
                                    <div className={classes.vpaBox}>
                                        <AccountBalanceWalletIcon style={{ fontSize: 18, color: ORANGE_DARK }} />
                                        <div style={{ minWidth: 0 }}>
                                            <div className={classes.factLabel}>Virtual account this QR should encode</div>
                                            <div className={`${classes.vpaValue} ${classes.mono}`}>{lead.upiVpa}</div>
                                        </div>
                                        <Tooltip title="Copy">
                                            <IconButton size="small" style={{ marginLeft: 'auto' }} onClick={() => copy(lead.upiVpa, 'VPA')}>
                                                <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                )}

                                <div className={classes.factLabel}>Stored QR url</div>
                                <div className={`${classes.urlBox} ${classes.mono}`}>
                                    {lead.paymentQrCode || 'No QR stored against this lead.'}
                                </div>

                                {previous && (
                                    <>
                                        <div className={classes.factLabel} style={{ marginTop: 12 }}>Replaced url</div>
                                        <div className={`${classes.urlBox} ${classes.mono}`} style={{ opacity: 0.75 }}>
                                            {previous.url || 'none'}
                                            {previous.encoded ? `   (encoded ${previous.encoded})` : ''}
                                        </div>
                                    </>
                                )}

                                <Divider style={{ margin: '18px 0 14px' }} />

                                {/* A QR that already encodes this lead's own number is
                                    correct, so there is nothing to do and nothing is
                                    offered - no remarks box, no button. Rebuilding it
                                    would only invalidate a QR the customer may already
                                    hold, for no gain. */}
                                {lead.status === 'ok' ? (
                                    <div className={classes.settledPanel}>
                                        <CheckCircleIcon style={{ color: GREEN, fontSize: 21, flexShrink: 0, marginTop: 1 }} />
                                        <div>
                                            <div className={classes.statusTitle} style={{ color: GREEN }}>
                                                Nothing to do for this lead
                                            </div>
                                            <div className={classes.statusDetail}>
                                                The stored QR already encodes{' '}
                                                <b className={classes.mono}>{lead.upiVpa}</b>, which is this
                                                lead&rsquo;s own virtual account. Payments scanned from it
                                                reconcile correctly, so it is not offered for regeneration.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <TextField
                                            className={classes.remarksField}
                                            label="Why is this QR being regenerated?"
                                            placeholder="e.g. QR was built from KA0226071, a different lead"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            disabled={lead.status === 'noNumber'}
                                            helperText={
                                                remarks.trim().length && remarks.trim().length < 5
                                                    ? 'A few more characters, please'
                                                    : 'Saved to the lead’s Deal Activity alongside the old and new QR urls'
                                            }
                                        />

                                        <div className={classes.actionRow}>
                                            <Button
                                                className={classes.regenerateBtn}
                                                onClick={() => setConfirmOpen(true)}
                                                disabled={!canRegenerate}
                                                startIcon={saving
                                                    ? <CircularProgress size={15} style={{ color: '#fff' }} />
                                                    : <AutorenewIcon />}
                                            >
                                                {saving ? 'Regenerating…' : 'Regenerate QR code'}
                                            </Button>

                                            {lead.status === 'noNumber' && (
                                                <span className={classes.dangerNote}>
                                                    <ErrorOutlineIcon style={{ fontSize: 15 }} />
                                                    No lead number, so no QR can be built.
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* --------------------------------------------- QR pane */}
                            <div className={classes.qrPane}>
                                {lead.paymentQrCode ? (
                                    <>
                                        <img
                                            className={classes.qrImg}
                                            src={lead.paymentQrCode}
                                            alt={`Payment QR for ${lead.uniqueLeadNumber}`}
                                        />
                                        <Button
                                            size="small"
                                            style={{ textTransform: 'none', color: ORANGE_DARK, fontWeight: 700 }}
                                            startIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                                            onClick={() => window.open(lead.paymentQrCode, '_blank', 'noopener')}
                                        >
                                            Open full size
                                        </Button>
                                    </>
                                ) : (
                                    <div className={classes.qrPlaceholder}>
                                        <CropFreeIcon style={{ fontSize: 30, color: '#cfd8e0' }} />
                                        No QR stored yet
                                    </div>
                                )}
                                <div className={classes.qrCaption}>
                                    {lead.qrEncodesLeadNumber
                                        ? <>Built from <b className={classes.mono}>{lead.qrEncodesLeadNumber}</b></>
                                        : 'Source lead number unknown'}
                                </div>
                            </div>
                        </div>
                    </Paper>
                </Grow>
            )}

            {/* --------------------------------------------------------- confirm */}
            <Dialog
                open={confirmOpen}
                onClose={() => !saving && setConfirmOpen(false)}
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    Regenerate this payment QR?
                </DialogTitle>
                <DialogContent>
                    <Typography className={classes.dialogLine}>
                        A new QR will be built and uploaded, and the lead will point at it. The
                        current QR stays in S3 but is no longer referenced.
                    </Typography>
                    <div className={`${classes.dialogCompare} ${classes.mono}`}>
                        <div>now&nbsp;&nbsp;&nbsp;&rarr; {lead?.qrEncodesLeadNumber || 'none'}</div>
                        <div style={{ color: GREEN, fontWeight: 700 }}>after&nbsp;&rarr; {lead?.uniqueLeadNumber}</div>
                    </div>
                    <Typography className={classes.dialogLine} style={{ marginTop: 10 }}>
                        Any QR already shared with this customer &mdash; printed, emailed or in a
                        quotation PDF &mdash; keeps pointing at the old virtual account. Make sure
                        the customer gets the new one.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: '8px 20px 18px' }}>
                    <Button className={classes.cancelBtn} onClick={() => setConfirmOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        className={classes.confirmBtn}
                        onClick={regenerate}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <AutorenewIcon />}
                    >
                        {saving ? 'Working…' : 'Yes, regenerate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setSnackInfo })(PaymentQrCode)
